import React, { useState } from "react";
import { Box, Button, Typography } from "@mui/material";
import Form from "react-bootstrap/Form";
import { toast } from "react-toastify";
import { saveErrorMessage } from "../../../common/saveMessage";
import { BsUpload } from "react-icons/bs";
import ImageUploading from "react-images-uploading";
import { Worker, Viewer } from "@react-pdf-viewer/core";
import { defaultLayoutPlugin } from "@react-pdf-viewer/default-layout";
import moment from "moment";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

import uploadDocumentEvents from "../../../api/uploadDocumentEvents";
import colorSchemes from "../../../constants/colorSchemes";
import FieldHint from "../../../packages/FieldHint";
import "@react-pdf-viewer/core/lib/styles/index.css";
import "@react-pdf-viewer/default-layout/lib/styles/index.css";

const AuctionForm = ({
  mode = "create",
  eventInfo,
  setEventInfo,
  countriesList,
  stateList,
  fetchStateData,
  setIsLoading,
  eventFormat,
  onSubmit,
  classes,
  onBack, // used in edit mode
  eventData = {}, // used in edit mode if needed
}) => {
  const maxNumber = 5;
  const isEnglishAuction = eventFormat === "english_auction" || eventFormat === "ENGLISH_AUCTION";
  const isDutchAuction = eventFormat === "dutch_auction" || eventFormat === "DUTCH_AUCTION";
  const isJapaneseAuction = eventFormat === "japanese_auction" || eventFormat === "JAPANESE_AUCTION";

  const [errors, setErrors] = useState({});
  const [images, setImages] = useState("");
  const [pdfFileError, setPdfFileError] = useState("");
  const [viewPdf, setViewPdf] = useState(null);

  const defaultLayoutPluginInstance = defaultLayoutPlugin();

  const validate = (name, value) => {
    const re = /^[A-Za-z ]+$/;
    let newErrors = { ...errors };
    switch (name) {
      case "state":
      case "city":
      case "country":
        if (!re.test(value)) {
          newErrors[name] = `${name.charAt(0).toUpperCase() + name.slice(1)} should contain only letters`;
        } else {
          delete newErrors[name];
        }
        break;
      default:
        break;
    }
    setErrors(newErrors);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    validate(name, value);
    if (name === "country") fetchStateData(value);
    setEventInfo((prev) => ({ ...prev, [name]: value }));
  };

  const onImageChange = (imageList) => {
    setImages(imageList);
    imageUpload(imageList);
  };

  const imageUpload = async (imageList) => {
    try {
      setIsLoading(true);
      for (let image of imageList) {
        const payload = { document: image["file"] };
        const resp = await uploadDocumentEvents(payload);
        if (resp && resp.data) {
          setEventInfo((prev) => ({ ...prev, eventImage: resp.data.documentId }));
          toast.success("Image uploaded.");
        } else {
            toast.error("Image not uploaded. Please try again.");
        }
      }
    } catch (error) {
      console.error("Image upload error:", error);
      toast.error(saveErrorMessage(error, "Image not uploaded. Please try again."));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePdfFileChange = async (e) => {
    let selectedFile = e.target.files[0];
    const fileType = ["application/pdf"];
    if (selectedFile) {
        if (fileType.includes(selectedFile.type)) {
            setIsLoading(true);
            let reader = new FileReader();
            reader.readAsDataURL(selectedFile);
            reader.onloadend = async (e) => {
                try {
                    const payload = { document: selectedFile };
                    const { data } = await uploadDocumentEvents(payload);
                    if (data) {
                        setEventInfo((prev) => ({ ...prev, auctionCatalogue: data.documentId }));
                        setViewPdf(e.target.result);
                        setPdfFileError("");
                        toast.success("Catalogue uploaded.");
                    }
                } catch (error) {
                    toast.error(saveErrorMessage(error, "Catalogue not uploaded. Please try again."));
                } finally {
                    setIsLoading(false);
                }
            };
        } else {
            setPdfFileError("Please select a PDF file.");
        }
    }
  };

  return (
    <div className={mode === "edit" ? classes.event : "container"}>
      {mode === "edit" && (
        <div className="row">
          <div className="col-md-4">
            <Typography
              className={classes.textHead}
              sx={{
                textAlign: "left",
                padding: "1em",
                fontSize: "20px",
                fontWeight: "600",
                color: colorSchemes.primaryColor,
              }}
            >
              <Button className="btn btn-group" onClick={onBack}>
                <ArrowBackIcon sx={{ color: "#000" }} />
              </Button>{" "}
              Event No: {eventData.eventNumber}
            </Typography>
          </div>
          <div className="col-md-4">
            <Typography
              className={classes.textHead}
              sx={{
                textAlign: "center",
                padding: "1em",
                fontSize: "20px",
                fontWeight: "600",
                color: colorSchemes.primaryColor,
              }}
            >
              Auction name {eventData.auctionTitle}
            </Typography>
          </div>
          <div className="col-md-4"></div>
        </div>
      )}

      <form onSubmit={onSubmit}>
        <div className="section-event mb-5">
          <div className="row">
            <div className="col-md-6">
              <Box className={classes.forms}>
                <Form.Group className="mb-3" controlId="auctionTitle">
                  <Form.Label className={classes.label}>Auction Title</Form.Label>
                  <Form.Control
                    className={classes.input}
                    type="text"
                    placeholder="Auction Title"
                    name="auctionTitle"
                    onChange={handleChange}
                    value={eventInfo.auctionTitle}
                    required
                  />
                </Form.Group>
              </Box>
            </div>
            <div className="col-md-6">
              <Box className={classes.forms}>
                <Form.Group className="mb-3" controlId="referenceNumber">
                  <Form.Label className={classes.label}>Reference Number <FieldHint id="referenceNumber" /></Form.Label>
                  <Form.Control
                    className={classes.input}
                    type="text"
                    name="referenceNumber"
                    onChange={handleChange}
                    value={eventInfo.referenceNumber}
                    maxLength={20}
                    required
                  />
                </Form.Group>
              </Box>
            </div>
            <div className="col-md-6">
              <Box className={classes.forms}>
                <Form.Label className={classes.label}>Description (Limit 250 characters)</Form.Label>
                <Form.Control
                  className={classes.input}
                  as="textarea"
                  name="description"
                  onChange={handleChange}
                  value={eventInfo.description}
                  style={{ height: "124px" }}
                  maxLength={250}
                  required
                />
              </Box>
            </div>
            <div className="col-md-6">
              <div className="mb-3">
                <Form.Label className={classes.label}>Event Type <FieldHint id="eventType" /></Form.Label>
                <Form.Select
                  className={classes.input}
                  name="eventType"
                  onChange={handleChange}
                  value={eventInfo.eventType}
                  required
                  disabled={mode === "edit"}
                >
                  <option value="">Select Event Type</option>
                  <option value="FORWARD_AUCTION">FORWARD AUCTION</option>
                  <option value="REVERSE_AUCTION">REVERSE AUCTION</option>
                </Form.Select>
              </div>
              <Box className={classes.forms}>
                <Form.Group controlId="ownerOfLots">
                  <Form.Label className={classes.label}>Owner Of Lots <FieldHint id="ownerOfLots" /></Form.Label>
                  <Form.Control
                    className={classes.input}
                    type="text"
                    name="ownerOfLots"
                    onChange={handleChange}
                    value={eventInfo.ownerOfLots}
                    required
                    placeholder="Owner Of Lots"
                  />
                </Form.Group>
              </Box>
            </div>
            <div className="col-md-6">
              <div className="mb-3" style={{ marginTop: ".8rem" }}>
                <Form.Label className={classes.label}>Auction visibility <FieldHint id="visibility" /></Form.Label>
                <Form.Select
                  className={classes.input}
                  name="visibility"
                  onChange={handleChange}
                  value={eventInfo.visibility}
                  required
                >
                  <option value="">Select auction visibility</option>
                  <option value="PUBLIC_AUCTION">Public auction</option>
                  <option value="PARTICIPANTS_ONLY_AUCTION">Participants only auction</option>
                  <option value="SELLER_BIDDERS_ONLY_AUCTION">Seller bidders only auction</option>
                </Form.Select>
              </div>
            </div>
            {isEnglishAuction && (
              <div className="col-md-6">
                <div className="mb-3" style={{ marginTop: ".8rem" }}>
                  <Form.Label className={classes.label}>Bid Type <FieldHint id="bidType" /></Form.Label>
                  <Form.Select
                    className={classes.input}
                    name="bidType"
                    onChange={handleChange}
                    value={eventInfo.bidType}
                    disabled={mode === "edit" && moment().isAfter(eventData.bidStartDateTime)}
                    required
                  >
                    <option value="">Select Bid Type</option>
                    <option value="RELATIVE_TO_LAST_BID_BY_ANY_USER">Relative to last bid by any user</option>
                    <option value="RELATIVE_TO_LAST_BID_BY_SELF">Relative to user's own last bid</option>
                  </Form.Select>
                </div>
              </div>
            )}
            <div className="col-md-6">
              <Box className={classes.forms}>
                <Form.Group className="mt-3">
                  <Form.Label className={classes.label}>Test Project <FieldHint id="testProject" /></Form.Label>
                  <Box sx={{ display: "flex" }}>
                    <Form.Check
                      name="testProject"
                      onChange={handleChange}
                      value="true"
                      checked={eventInfo.testProject === "true" || eventInfo.testProject === true}
                      type="radio"
                      label="Yes"
                    />
                    <Form.Check
                      className={classes.radio}
                      name="testProject"
                      onChange={handleChange}
                      value="false"
                      checked={eventInfo.testProject === "false" || eventInfo.testProject === false}
                      type="radio"
                      label="No"
                    />
                  </Box>
                </Form.Group>
              </Box>
            </div>

            <div className="col-md-12">
              <Box className={classes.break} />
            </div>

            <div className="col-md-6">
              <Box className={classes.forms}>
                <Form.Group controlId="streetOne">
                  <Form.Label className={classes.label}>Venue Address</Form.Label>
                  <Form.Control
                    className={classes.input}
                    type="text"
                    name="streetOne"
                    onChange={handleChange}
                    value={eventInfo.streetOne}
                    required
                    placeholder="Street Address"
                  />
                </Form.Group>
              </Box>
            </div>
            <div className="col-md-6">
              <Box className={classes.forms}>
                <Form.Group controlId="streetTwo">
                  <Form.Label className={classes.label} style={{ visibility: "hidden" }}>Venue Address</Form.Label>
                  <Form.Control
                    className={classes.input}
                    type="text"
                    name="streetTwo"
                    onChange={handleChange}
                    value={eventInfo.streetTwo}
                    required
                    placeholder="Street Address Second"
                  />
                </Form.Group>
              </Box>
            </div>
            <div className="col-md-6">
              <Box className={classes.forms}>
                <Form.Group controlId="city">
                  <Form.Label className={classes.label} style={{ visibility: "hidden" }}>Venue Address</Form.Label>
                  <Form.Control
                    className={classes.input}
                    type="text"
                    name="city"
                    onChange={handleChange}
                    value={eventInfo.city}
                    required
                    placeholder="City"
                  />
                  {errors.city && <span>{errors.city}</span>}
                </Form.Group>
              </Box>
            </div>
            <div className="col-md-6">
              <Box className={classes.forms}>
                <Form.Group controlId="postalCode">
                  <Form.Label className={classes.label} style={{ visibility: "hidden" }}>Venue Address</Form.Label>
                  <Form.Control
                    className={classes.input}
                    type="text"
                    name="postalCode"
                    onChange={handleChange}
                    value={eventInfo.postalCode}
                    required
                    placeholder="Postal Code"
                  />
                </Form.Group>
              </Box>
            </div>
            <div className="col-md-6">
              <Box className={classes.forms}>
                <Form.Group controlId="country">
                  <Form.Label className={classes.label} style={{ visibility: "hidden" }}>Venue Address</Form.Label>
                  <Form.Select
                    className={classes.input}
                    name="country"
                    onChange={handleChange}
                    value={eventInfo.country}
                    required
                  >
                    <option value="">Country</option>
                    {countriesList.list.map((country) => (
                      <option key={country.id} value={country.id}>{country.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Box>
            </div>
            <div className="col-md-6">
              <Box className={classes.forms}>
                <Form.Group controlId="province">
                  <Form.Label className={classes.label} style={{ visibility: "hidden" }}>Venue Address</Form.Label>
                  <Form.Select
                    className={classes.input}
                    name="province"
                    onChange={handleChange}
                    value={eventInfo.province}
                    required
                  >
                    <option value="">Province</option>
                    {stateList.list.map((state) => (
                      <option key={state.id} value={state.id}>{state.name}</option>
                    ))}
                  </Form.Select>
                </Form.Group>
              </Box>
            </div>

            <div className="col-md-12">
              <Box className={classes.break} />
            </div>

            <div className="col-md-12">
              <div className={classes.uploader}>
                <div className="row">
                  <div className="col-md-6">
                    {mode === "edit" && eventData.eventImageUrl && (
                        <div className="row mb-2">
                            <div className="col-md-12">
                                Old Event Image
                                <div className="image-item">
                                    <img src={eventData.eventImageUrl} alt="" width="100" className={classes.imagesStyle} />
                                </div>
                            </div>
                        </div>
                    )}
                    Event Image (JPG,JPEG,PNG) <FieldHint id="eventImage" />
                    <p className={classes.imageSize}>Dimension: 260px X 200px</p>
                    <ImageUploading
                      value={images}
                      onChange={onImageChange}
                      maxNumber={maxNumber}
                      dataURLKey="data_url"
                      acceptType={["jpg", "jpeg", "png"]}
                    >
                      {({ imageList, onImageUpload, dragProps, errors }) => (
                        <>
                          <div className="upload__image-wrapper">
                            <Box sx={{ width: "100%" }}>
                              <Button
                                variant="contained"
                                component="label"
                                className={classes.upload}
                                sx={{ display: "block", textAlign: "right" }}
                                onClick={onImageUpload}
                                {...dragProps}
                              >
                                <BsUpload className={classes.uploadIcon} />
                              </Button>
                            </Box>
                            <div className="row">
                              {imageList.map((image, index) => (
                                <div key={index} className="col-md-4">
                                  <div className="image-item">
                                    <img src={image["data_url"]} alt="" width="100" className={classes.imagesStyle} />
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </>
                      )}
                    </ImageUploading>
                  </div>

                  <div className="col-md-6">
                    Catalogue (PDF) <FieldHint id="catalogue" />
                    <input
                      type="file"
                      accept=".pdf"
                      style={{ marginTop: "30px" }}
                      className="form-control"
                      required={mode === "create"}
                      onChange={handlePdfFileChange}
                    />
                    {pdfFileError && <div className="error-msg">{pdfFileError}</div>}
                    <div style={{ height: "30%" }} className="pdf-container">
                      {viewPdf || (mode === "edit" && eventData.auctionCatalogueUrl) ? (
                        <div style={{ display: "block", height: "30em" }}>
                          <Worker workerUrl="https://unpkg.com/pdfjs-dist@3.11.174/build/pdf.worker.min.js">
                            <Viewer
                              fileUrl={viewPdf || eventData.auctionCatalogueUrl}
                              plugins={[defaultLayoutPluginInstance]}
                            />
                          </Worker>
                        </div>
                      ) : (
                        <p style={{ height: "2em" }}>No Pdf Selected</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-6">
              <div className={classes.saveBtn}>
                <Button
                  className={classes.next}
                  type="submit"
                  disabled={Boolean(eventData?.editLocked)}
                >
                  {mode === "create" ? "Save & Next" : "Save changes"}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default AuctionForm;
